import { allNavItems, moduleLabel, moduleOf, navLabel, type NavItem } from '@/config/navigation'
import type { Translate } from '@/i18n'
import { cn } from '@/lib/cn'
import { pickLocalized } from '@/lib/format'
import type { OnlineUser } from '@/services/core/presence.service'

/**
 * The page behind an admin route, as «Module › Page» in the viewer's
 * language: the listed or detail page whose path is the longest prefix of it
 * (/procurement/proformas/42 → Закупка › Проформы). The raw route when no page
 * matches.
 */
export function presencePageLabel(path: string, t: Translate) {
  let best: NavItem | undefined
  for (const item of allNavItems) {
    const matches = item.path === '/' ? path === '/' : path === item.path || path.startsWith(`${item.path}/`)
    if (matches && (!best || item.path.length > best.path.length)) best = item
  }
  if (!best) return path
  const page = navLabel(t, best)
  return best.path === '/' ? page : `${moduleLabel(t, moduleOf(best))} › ${page}`
}

/** 45 s → «<1 min», 720 → «12 min», 3900 → «1 h 05 min». */
export function formatPresenceDuration(seconds: number, t: Translate) {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  if (minutes < 1) return t('layout.presence.lessThanMinute')
  if (minutes < 60) return t('layout.presence.minutes', { count: minutes })
  return t('layout.presence.hoursMinutes', { hours: Math.floor(minutes / 60), minutes: String(minutes % 60).padStart(2, '0') })
}

/** Seconds from an ISO time to now. */
export const secondsSince = (iso: string) => Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)

/** The status dot laid on an avatar: green active, amber idle, grey offline. */
export function PresenceDot({ status, className }: { status: OnlineUser['status'] | 'offline'; className?: string }) {
  return (
    <span
      className={cn(
        'absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-canvas',
        status === 'active' ? 'bg-success' : status === 'idle' ? 'bg-warning' : 'bg-subtle',
        className,
      )}
    />
  )
}

/** «Кладовщик · Ташкент» in the viewer's language. */
export function presenceRoleLine(user: OnlineUser, locale: Parameters<typeof pickLocalized>[2]) {
  return [pickLocalized(user, 'role_name', locale), pickLocalized(user, 'branch_name', locale)].filter(Boolean).join(' · ')
}

/** «Закупка › Проформы · 12 min» or «… · idle 8 min». */
export function presencePageLine(user: OnlineUser, t: Translate) {
  const page = presencePageLabel(user.path, t)
  return user.status === 'idle'
    ? `${page} · ${t('layout.presence.idleFor', { duration: formatPresenceDuration(user.idle_for_seconds, t) })}`
    : `${page} · ${t('layout.presence.onPage', { duration: formatPresenceDuration(secondsSince(user.page_since), t) })}`
}
